export interface AnalyticsConsentCopy {
  title: string;
  description: string;
  accept: string;
  reject: string;
  settings: string;
  privacy: string;
  currentStatus: string;
  grantedStatus: string;
  deniedStatus: string;
  notSelectedStatus: string;
  updated: string;
}

const consentCopy: Record<string, AnalyticsConsentCopy> = {
  en: {
    title: "Optional analytics",
    description:
      "Allow analytics cookies and session continuity on public marketing pages so we can improve AriaFlow. Without permission, we use only limited cookieless measurement. Essential product functions work either way.",
    accept: "Allow analytics",
    reject: "Continue without analytics cookies",
    settings: "Cookie settings",
    privacy: "Privacy policy",
    currentStatus: "Current choice",
    grantedStatus: "Analytics allowed",
    deniedStatus: "Limited cookieless measurement",
    notSelectedStatus: "No choice saved",
    updated: "Cookie settings updated.",
  },
  pt: {
    title: "Análises opcionais",
    description:
      "Permita cookies de análise e continuidade de sessão nas páginas públicas para nos ajudar a melhorar a AriaFlow. Sem permissão, usamos apenas medição limitada sem cookies. As funções essenciais continuam disponíveis.",
    accept: "Permitir análises",
    reject: "Continuar sem cookies de análise",
    settings: "Definições de cookies",
    privacy: "Política de privacidade",
    currentStatus: "Escolha atual",
    grantedStatus: "Análises permitidas",
    deniedStatus: "Medição limitada sem cookies",
    notSelectedStatus: "Nenhuma escolha guardada",
    updated: "Definições de cookies atualizadas.",
  },
  es: {
    title: "Analítica opcional",
    description:
      "Permite cookies de analítica y continuidad de sesión en páginas públicas para ayudarnos a mejorar AriaFlow. Sin permiso, usamos solo medición limitada sin cookies. Las funciones esenciales siguen disponibles.",
    accept: "Permitir analítica",
    reject: "Continuar sin cookies de analítica",
    settings: "Configurar cookies",
    privacy: "Política de privacidad",
    currentStatus: "Selección actual",
    grantedStatus: "Analítica permitida",
    deniedStatus: "Medición limitada sin cookies",
    notSelectedStatus: "No hay ninguna selección guardada",
    updated: "Configuración de cookies actualizada.",
  },
  de: {
    title: "Optionale Analyse",
    description:
      "Erlaube Analyse-Cookies und sitzungsübergreifende Messung auf öffentlichen Seiten, damit wir AriaFlow verbessern können. Ohne Zustimmung verwenden wir nur eine eingeschränkte cookielose Messung. Wesentliche Produktfunktionen bleiben verfügbar.",
    accept: "Analyse erlauben",
    reject: "Ohne Analyse-Cookies fortfahren",
    settings: "Cookie-Einstellungen",
    privacy: "Datenschutz",
    currentStatus: "Aktuelle Auswahl",
    grantedStatus: "Analyse erlaubt",
    deniedStatus: "Eingeschränkte cookielose Messung",
    notSelectedStatus: "Keine Auswahl gespeichert",
    updated: "Cookie-Einstellungen aktualisiert.",
  },
  fr: {
    title: "Mesure d’audience facultative",
    description:
      "Autorisez les cookies de mesure et la continuité de session sur les pages publiques afin de nous aider à améliorer AriaFlow. Sans autorisation, seule une mesure limitée sans cookie est utilisée. Les fonctions essentielles restent disponibles.",
    accept: "Autoriser la mesure",
    reject: "Continuer sans cookies de mesure",
    settings: "Paramètres des cookies",
    privacy: "Politique de confidentialité",
    currentStatus: "Choix actuel",
    grantedStatus: "Mesure autorisée",
    deniedStatus: "Mesure limitée sans cookie",
    notSelectedStatus: "Aucun choix enregistré",
    updated: "Paramètres des cookies mis à jour.",
  },
  it: {
    title: "Analisi facoltative",
    description:
      "Consenti cookie analitici e continuità della sessione sulle pagine pubbliche per aiutarci a migliorare AriaFlow. Senza autorizzazione usiamo solo misurazioni limitate senza cookie. Le funzioni essenziali restano disponibili.",
    accept: "Consenti analisi",
    reject: "Continua senza cookie analitici",
    settings: "Impostazioni cookie",
    privacy: "Informativa sulla privacy",
    currentStatus: "Scelta attuale",
    grantedStatus: "Analisi consentite",
    deniedStatus: "Misurazione limitata senza cookie",
    notSelectedStatus: "Nessuna scelta salvata",
    updated: "Impostazioni cookie aggiornate.",
  },
  ja: {
    title: "任意のアクセス解析",
    description:
      "AriaFlowの改善のため、公開ページで解析Cookieとセッション継続計測を許可します。許可しない場合は、Cookieを使わない限定的な計測のみを行います。主要な製品機能はどちらでも利用できます。",
    accept: "解析を許可",
    reject: "解析Cookieなしで続行",
    settings: "Cookie設定",
    privacy: "プライバシーポリシー",
    currentStatus: "現在の選択",
    grantedStatus: "解析を許可",
    deniedStatus: "Cookieを使わない限定計測",
    notSelectedStatus: "選択は保存されていません",
    updated: "Cookie設定を更新しました。",
  },
  ko: {
    title: "선택적 분석",
    description:
      "AriaFlow 개선을 위해 공개 페이지에서 분석 쿠키와 세션 연속 측정을 허용합니다. 허용하지 않으면 쿠키 없는 제한적 측정만 사용합니다. 필수 제품 기능은 그대로 사용할 수 있습니다.",
    accept: "분석 허용",
    reject: "분석 쿠키 없이 계속",
    settings: "쿠키 설정",
    privacy: "개인정보 처리방침",
    currentStatus: "현재 선택",
    grantedStatus: "분석 허용",
    deniedStatus: "쿠키 없는 제한적 측정",
    notSelectedStatus: "저장된 선택 없음",
    updated: "쿠키 설정이 업데이트되었습니다.",
  },
  nl: {
    title: "Optionele analyse",
    description:
      "Sta analysecookies en sessiecontinuïteit op openbare pagina’s toe om AriaFlow te helpen verbeteren. Zonder toestemming gebruiken we alleen beperkte cookieloze meting. Essentiële productfuncties blijven beschikbaar.",
    accept: "Analyse toestaan",
    reject: "Doorgaan zonder analysecookies",
    settings: "Cookie-instellingen",
    privacy: "Privacybeleid",
    currentStatus: "Huidige keuze",
    grantedStatus: "Analyse toegestaan",
    deniedStatus: "Beperkte cookieloze meting",
    notSelectedStatus: "Geen keuze opgeslagen",
    updated: "Cookie-instellingen bijgewerkt.",
  },
  pl: {
    title: "Opcjonalna analityka",
    description:
      "Zezwól na pliki cookie analityki i ciągłość sesji na stronach publicznych, aby pomóc nam ulepszać AriaFlow. Bez zgody używamy tylko ograniczonego pomiaru bez plików cookie. Podstawowe funkcje produktu pozostają dostępne.",
    accept: "Zezwól na analitykę",
    reject: "Kontynuuj bez plików cookie analityki",
    settings: "Ustawienia plików cookie",
    privacy: "Polityka prywatności",
    currentStatus: "Bieżący wybór",
    grantedStatus: "Analityka dozwolona",
    deniedStatus: "Ograniczony pomiar bez plików cookie",
    notSelectedStatus: "Nie zapisano wyboru",
    updated: "Ustawienia plików cookie zostały zaktualizowane.",
  },
  ru: {
    title: "Необязательная аналитика",
    description:
      "Разрешите аналитические cookie и непрерывность сеанса на публичных страницах, чтобы помочь нам улучшать AriaFlow. Без разрешения используется только ограниченное измерение без cookie. Основные функции продукта остаются доступными.",
    accept: "Разрешить аналитику",
    reject: "Продолжить без аналитических cookie",
    settings: "Настройки cookie",
    privacy: "Политика конфиденциальности",
    currentStatus: "Текущий выбор",
    grantedStatus: "Аналитика разрешена",
    deniedStatus: "Ограниченное измерение без cookie",
    notSelectedStatus: "Выбор не сохранён",
    updated: "Настройки cookie обновлены.",
  },
  zh: {
    title: "可选分析",
    description:
      "允许我们在公开营销页面使用分析 Cookie 和跨会话统计，以改进 AriaFlow。未获允许时仅进行有限的无 Cookie 测量，必要的产品功能不受影响。",
    accept: "允许分析",
    reject: "不使用分析 Cookie，继续访问",
    settings: "Cookie 设置",
    privacy: "隐私政策",
    currentStatus: "当前选择",
    grantedStatus: "已允许分析",
    deniedStatus: "仅限无 Cookie 测量",
    notSelectedStatus: "尚未保存选择",
    updated: "Cookie 设置已更新。",
  },
  "zh-tw": {
    title: "選用分析",
    description:
      "允許我們在公開行銷頁面使用分析 Cookie 與跨工作階段統計，以改善 AriaFlow。未獲允許時僅進行有限的無 Cookie 測量，必要的產品功能不受影響。",
    accept: "允許分析",
    reject: "不使用分析 Cookie，繼續瀏覽",
    settings: "Cookie 設定",
    privacy: "隱私權政策",
    currentStatus: "目前選擇",
    grantedStatus: "已允許分析",
    deniedStatus: "僅限無 Cookie 測量",
    notSelectedStatus: "尚未儲存選擇",
    updated: "Cookie 設定已更新。",
  },
};

export const getAnalyticsConsentCopy = (locale: string) =>
  consentCopy[locale] || consentCopy.en;
